import React, { useState, useEffect } from 'react';
import GoogleSignInButton, { OrDivider } from '../components/auth/GoogleSignInButton';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Mail, Lock, ArrowRight, Sparkles, Check, Shield, User, Building, Calculator, MessageCircle, Receipt, Zap, DollarSign, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import LanguageSwitcher from '../components/LanguageSwitcher';
import AdminCodeStep from '../components/auth/AdminCodeStep';
import RestoreRequestPanel from '../components/auth/RestoreRequestPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const SUBSCRIPTION_API = `${API}/api/subscription`;

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [foundingStatus, setFoundingStatus] = useState(null);
  const [adminChallenge, setAdminChallenge] = useState(null);
  const [showRestore, setShowRestore] = useState(false);

  // Fetch founding member slot count — public endpoint, no auth
  useEffect(() => {
    axios.get(`${SUBSCRIPTION_API}/founding-status`)
      .then(r => setFoundingStatus(r.data))
      .catch(() => {});
  }, []);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(`${API}/api/auth/resend-verification`, { email });
      toast.success(t('toast.verification_email_sent_check_your_inbox'));
    } catch (error) {
      toast.error(t('toast.failed_to_send_verification_email'));
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowVerificationMessage(false);
    
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setAdminChallenge(result);
        return;
      }
      toast.success(t('toast.welcome_back'));
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid credentials';
      
      // Check if error is about email verification
      if (error.response?.status === 403 && detail.includes('scheduled for deletion')) {
        setShowRestore(true);
      } else if (error.response?.status === 403 && detail.includes('verify')) {
        setShowVerificationMessage(true);
        toast.error(t('toast.please_verify_your_email_first'));
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: DollarSign, text: t('landing.feature_tax_leak', 'Tax Leak Audit — find missed savings') },
    { icon: Calculator, text: t('landing.feature_3_desc') },
    { icon: MessageCircle, text: t('landing.feature_ai_advisors', 'Multiple AI tax advisors') },
    { icon: Receipt, text: t('landing.feature_2_desc') },
    { icon: Zap, text: t('landing.feature_scorp', 'S-Corp optimization analysis') },
    { icon: Building, text: t('landing.feature_1099', '1099 contractor management') }
  ];

  return (
    <div className="min-h-screen bg-[#0A0E17] overflow-x-hidden">

      {/* ── Ambient background glows ── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-emerald-500/6 rounded-full blur-[140px] translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-emerald-900/20 rounded-full blur-[120px] -translate-x-1/4 translate-y-1/4" />
      </div>

      {/* ── Sticky glassmorphism header ── */}
      <header className="fixed top-0 w-full z-50 bg-[#0A0E17]/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="login-logo">
            <div className="w-8 h-8 relative flex items-center justify-center">
              <div className="absolute inset-0 bg-emerald-500 rounded-lg transform rotate-45" />
              <div className="relative w-3 h-3 bg-white z-10" />
            </div>
            <span className="text-lg font-semibold tracking-tight">
              <span className="text-white">Solely</span><span className="text-emerald-400">Tax</span>
            </span>
          </div>

          {!showLoginForm && (
            <nav className="flex items-center gap-2">
              <Button
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-white/5 hidden sm:flex text-sm"
                onClick={() => navigate('/audit')}
              >
                Free Audit
              </Button>
              <Button
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-white/5 hidden sm:flex text-sm"
                onClick={() => navigate('/how-it-works')}
              >
                How It Works
              </Button>
              <Button
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-sm"
                onClick={() => setShowLoginForm(true)}
              >
                {t('auth.login')}
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* ── Main content ── */}
      <main className="max-w-7xl mx-auto px-6 pt-28 pb-16 relative z-10">
        {!showLoginForm ? (

          /* ── Landing view — three-section grid ── */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-x-16 lg:gap-y-8 lg:items-start">

            {/* ── 1. HEADLINE (first everywhere) ── */}
            <div className="lg:col-span-7 lg:row-start-1 flex flex-col items-center text-center lg:items-start lg:text-left gap-5">

              {/* Badge */}
              <div className="inline-flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/8 text-emerald-400 px-4 py-1.5 rounded-full text-sm font-medium tracking-wide">
                <Sparkles className="w-3.5 h-3.5" />
                {t('landing.badge_ai', 'Tax Optimization OS')}
              </div>

              {/* H1 */}
              <div className="space-y-5">
                <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter leading-tight">
                  <span className="bg-amber-400 text-slate-900 px-3 py-1 rounded-md font-black">Stop Guessing</span>{' '}
                  <span className="text-white">What You Owe the IRS.</span>
                </h1>
                <p className="text-base lg:text-lg text-slate-400 leading-relaxed max-w-lg">
                  {t('landing.hero_subtitle')}
                </p>
              </div>
            </div>

            {/* ── 2. CARD (second on mobile · right column spanning rows 1+2 on desktop) ── */}
            <div className="lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:row-span-2 lg:mt-14">
              <div className="bg-slate-900/60 backdrop-blur-2xl border border-slate-700/50 rounded-2xl p-8 shadow-[0_0_60px_-15px_rgba(16,185,129,0.18)]">

                <div className="mb-6">
                  <h2 className="text-xl font-bold text-white mb-1.5">Run Your Free Tax Leak Audit</h2>
                  <p className="text-slate-400 text-sm">Get your Tax Leak Score in 60 seconds</p>
                </div>

                <Button
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold py-6 text-base rounded-xl shadow-lg shadow-emerald-500/20 transition-all"
                  onClick={() => navigate('/register')}
                  data-testid="create-account-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Create Free Account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <OrDivider variant="dark" />
                <GoogleSignInButton variant="dark" testId="landing-google-signin-btn" />

                <div className="mt-4 p-3 bg-emerald-500/5 border border-emerald-500/15 rounded-xl text-center">
                  <p className="text-emerald-400/80 text-xs">
                    <span className="font-semibold text-emerald-400">No credit card required</span>{' '}
                    · 30-day free trial · Cancel anytime
                  </p>
                </div>

                <div className="mt-6 pt-5 border-t border-slate-800 text-center">
                  {foundingStatus && (
                    <div className="mb-4 text-left" data-testid="founding-ticker">
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="flex items-center gap-1.5 text-amber-400 font-medium">
                          <Users className="w-3 h-3" />
                          {foundingStatus.used === 0
                            ? 'Be one of the first 100 founding members'
                            : `${foundingStatus.used} founding ${foundingStatus.used === 1 ? 'member' : 'members'} joined`}
                        </span>
                        <span className="text-slate-500">{foundingStatus.remaining} of 100 left</span>
                      </div>
                      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-400 rounded-full transition-all duration-700"
                          style={{ width: `${Math.max(2, foundingStatus.percent_claimed)}%` }}
                          data-testid="founding-progress-bar"
                        />
                      </div>
                    </div>
                  )}
                  <p className="text-slate-500 text-sm">
                    Already have an account?{' '}
                    <button
                      onClick={() => setShowLoginForm(true)}
                      className="text-emerald-400 hover:text-emerald-300 font-medium"
                      data-testid="show-login-btn"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </div>

              {/* Removed testimonial - Carlotta is an AI advisor, not an independent CPA */}
            </div>

            {/* ── 3. FEATURES (third on mobile · left column row 2 on desktop) ── */}
            <div className="lg:col-span-7 lg:col-start-1 lg:row-start-2 flex flex-col items-center text-center lg:items-start lg:text-left gap-5">

              {/* Feature pills */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full max-w-lg mx-auto lg:mx-0">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-slate-900/60 border border-slate-800 text-slate-300 rounded-xl px-4 py-3 text-sm font-medium hover:bg-slate-800/60 hover:border-slate-700 transition-colors"
                  >
                    <feature.icon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* How It Works */}
              <button
                onClick={() => navigate('/how-it-works')}
                className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium text-sm transition-colors group"
              >
                <Zap className="w-4 h-4" />
                See How It Works — 8 Steps with Screenshots
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>

              {/* Trust indicators */}
              <div className="flex items-center gap-5 text-xs font-medium text-slate-500 uppercase tracking-widest">
                <div className="flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-emerald-500" />
                  <span>256-bit SSL</span>
                </div>
                <div className="w-px h-4 bg-slate-800" />
                <div className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Cancel Anytime</span>
                </div>
              </div>
            </div>
          </div>

        ) : (
          /* ── Login form ── */
          <div className="max-w-md mx-auto">
            {showRestore ? (
              <RestoreRequestPanel email={email} onBack={() => { setShowRestore(false); setPassword(''); }} />
            ) : adminChallenge ? (
              <AdminCodeStep
                challenge={adminChallenge}
                onChallengeChange={setAdminChallenge}
                onCancel={() => { setAdminChallenge(null); setPassword(''); }}
                onSuccess={() => { toast.success(t('toast.welcome_back')); navigate('/dashboard'); }}
              />
            ) : (
            <Card className="bg-slate-900 border-slate-800 shadow-2xl">
              <CardHeader className="text-center pb-4">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-9 h-9 relative flex items-center justify-center">
                    <div className="absolute inset-0 bg-emerald-500 rounded-lg transform rotate-45" />
                    <div className="relative w-3.5 h-3.5 bg-white z-10" />
                  </div>
                  <span className="text-xl font-semibold tracking-tight">
                    <span className="text-white">Solely</span>
                    <span className="text-emerald-400">Tax</span>
                  </span>
                </div>
                <CardTitle className="text-2xl font-bold text-white">{t('auth.welcome_back')}</CardTitle>
                <CardDescription className="text-slate-400">
                  {t('auth.sign_in_continue')}
                </CardDescription>
              </CardHeader>

              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-300 font-medium">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                        required
                        data-testid="login-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-slate-300 font-medium">{t('auth.password')}</Label>
                      <Link to="/forgot-password" className="text-sm text-emerald-400 hover:text-emerald-300">
                        {t('auth.forgot_password')}
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                        required
                        data-testid="login-password"
                      />
                    </div>
                  </div>

                  {showVerificationMessage && (
                    <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-4">
                      <p className="text-amber-300 text-sm mb-3">
                        {t('auth.verify_email_message')}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleResendVerification}
                        disabled={resendingVerification}
                        className="w-full border-amber-700/50 text-amber-300 hover:bg-amber-900/30"
                      >
                        {resendingVerification ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('auth.sending')}</>
                        ) : (
                          <><Mail className="w-4 h-4 mr-2" />{t('auth.resend_verification')}</>
                        )}
                      </Button>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold py-5"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? t('auth.signing_in') : t('auth.sign_in')}
                    {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
                  </Button>
                </form>

                <OrDivider variant="dark" />
                <GoogleSignInButton variant="dark" testId="login-google-signin-btn" />
              </CardContent>

              <CardFooter className="justify-center flex-col gap-4">
                <p className="text-slate-500 text-sm">
                  {t('auth.no_account')}{' '}
                  <button
                    onClick={() => setShowLoginForm(false)}
                    className="text-emerald-400 hover:text-emerald-300 font-medium"
                  >
                    {t('auth.create_one')}
                  </button>
                </p>
                <Button
                  variant="ghost"
                  className="text-slate-500 hover:text-slate-300"
                  onClick={() => setShowLoginForm(false)}
                >
                  ← {t('auth.back_home')}
                </Button>
              </CardFooter>
            </Card>
            )}
          </div>
        )}
      </main>

      {/* ── Legal footer ── */}
      <footer className="relative z-10 border-t border-slate-800/50 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-center gap-6 text-xs text-slate-600">
          <button onClick={() => navigate('/terms')} className="hover:text-slate-400 transition-colors">
            Terms of Service
          </button>
          <span>·</span>
          <button onClick={() => navigate('/privacy')} className="hover:text-slate-400 transition-colors">
            Privacy Policy
          </button>
          <span>·</span>
          <a href="mailto:support@solelytax.ai" className="hover:text-slate-400 transition-colors">
            Support
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Login;
